import Conditional from "@repo/shared-ui/Conditional";
import { isMobile } from "@repo/shared-utils/mobile-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { BannerProps } from "../../interfaces/Banner.interface";
import AnalyticsBannerLink from "./AnalyticsBannerLink";
import BannerCarousel from "./BannerCarousel";
const Banner: React.FC<BannerProps> = ({ bannerizationcarousel }) => {
  const { showByDevice, bannerItems, variation, moduleName, sectionName, isLoop, autoplay, autoTimer, customClass, inlineStyle } = bannerizationcarousel;

  const showComponent = (): boolean => {
    if (showByDevice === "both") return true;
    if (showByDevice === "onlyDesktop") return !isMobile;
    if (showByDevice === "onlyMobile") return isMobile;
    return false;
  };

  const parseStyleString = (styleString?: string): React.CSSProperties => {
    if (!styleString) {
      return {};
    }

    return styleString.split(";").reduce((acc, rule) => {
      if (!rule.trim()) return acc;
      const [property, value] = rule.split(":");
      if (!property || !value) return acc;
      const camelProp = property
        .trim()
        .replace(/-([a-z])/g, (_, char) => char.toUpperCase());

      (acc as Record<string, any>)[camelProp] = value.trim();
      return acc;
    }, {} as React.CSSProperties);
  };

  return (
  <>
    <Conditional if={showComponent()}>
      {variation === "stripBanner" ? (
        <section className={`strip-banner border-b border-[#cccccc] ${customClass ?? ""}`}>
          {bannerItems.map((item, index) => (
            <AnalyticsBannerLink
              key={"strip-" + index + "-" + item.bannerTitle}
              linkUrl={isOpenInApp() ? item.bannerCTA : item.webBannerCTA}
              target={item.webCtaTarget ?? "_blank"}
              className="block"
              bannerData={{
                ctaText: item.bannerCtaText,
                altCtaText: item.bannerAltText,
                sectionTitle: item.bannerTitle,
                index: index,
                moduleName: moduleName,
                sectionName: sectionName,
                totalBanners: bannerItems.length,
              }}
            >
              <picture>
                <source srcSet={item.desktopBanner} media="(min-width: 1025px)" />
                <img
                  className="strip-banner__image w-full"
                  src={item.bannerWebImage}
                  alt={item.bannerAltText}
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "auto"}
                  decoding="async"
                />
              </picture>
            </AnalyticsBannerLink>
          ))}
        </section>
      ) : (
        <section className={`border-b border-[#cccccc] ${customClass ?? ""}`} style={parseStyleString(inlineStyle)}>
          <BannerCarousel 
            moduleName={moduleName} 
            sectionName={sectionName} 
            isLoop={isLoop} 
            autoplay={autoplay} 
            autoTimer={autoTimer}
          >
            {bannerItems.map((item, index) => {
              return (
                <AnalyticsBannerLink
                  key={`banner-${index}-${item.bannerTitle}`}
                  linkUrl={isOpenInApp() ? item.appLink : item.webLink}
                  target={item.webCtaTarget ?? "_blank"}
                  className="relative block h-[235px] md:h-64 w-full"
                  bannerData={{
                    ctaText: item.bannerCtaText,
                    altCtaText: item.bannerAltText,
                    sectionTitle: item.bannerTitle,
                    index: index,
                    moduleName: moduleName,
                    sectionName: sectionName,
                    totalBanners: bannerItems.length,
                  }}
                >
                  <picture>
                    <source srcSet={item.bannerWebImage} media="(max-width: 767px)" />
                    <source srcSet={item.desktopBanner} media="(min-width: 768px)" />
                    <img
                      className="absolute inset-0 h-full w-full object-fill"
                      src={item.desktopBanner}
                      alt={item.bannerAltText}
                      loading={index === 0 ? "eager" : "lazy"}
                      fetchPriority={index === 0 ? "high" : "auto"}
                      decoding="async"
                    />
                  </picture>
                  <div className="text-white absolute top-4 left-[13px] md:left-[40px]">
                    <div className="flex flex-col items-start md:flex-row md:items-center md:gap-4">
                      <p className="text-[20px] lg:text-[32px] font-medium">
                        {item.bannerTitle}
                      </p>
                      {item.bannerDescription1 && (
                        <h2 className="text-[16px] lg:text-[32px] font-medium">
                          {item.bannerDescription1}
                        </h2>
                      )}
                    </div>
                    <ul className="mt-3 list-inside list-disc text-[14px] md:text-[22px]">
                      {item.bannerDescription2 && <li>{item.bannerDescription2}</li>}
                      {item.bannerDescription3 && <li>{item.bannerDescription3}</li>}
                      {item.bannerDescription4 && <li>{item.bannerDescription4}</li>}
                    </ul>
                  </div>
                  <span className="absolute leading-tight md:leading-none bottom-[38px] left-[13px] md:left-[40px] rounded-[20px] border-[0.5px] border-black
                      bg-white text-black 
                      px-[12px] py-[5px] md:px-[16px] md:py-[7px] 
                      text-[15px] md:text-[22px]">
                    {item.bannerCtaText}
                  </span>
                </AnalyticsBannerLink>
              );
            })}
          </BannerCarousel>
        </section>
      )}
    </Conditional>
   </>
  );
};

export default Banner;
